"use client";
import AlertsAndTriggers from "../modal/AlertsAndTriggers";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import { useEffect, useRef, useState } from "react";
import CustomDropdown from "@/components/ui/CustomDropdown";
import { apiGet } from "@/utils/apiClient";
import { formatDate, calculateReturn } from "@/utils/commonFunctions";
import { format } from "date-fns";
import Icon from "@/components/icons/Icons";
import CustomCalendar from "@/components/ui/CustomCalendar";
import Image from "next/image";
import { AllBenchmarkListItem } from "@/type/commonTypes";
import { NODE_API_URL } from "@/utils/gloableVariables";

type ChartDataType = {
  schemeCode: string;
  schemeName?: string | null;
  currentNav: string | null;
  currentNavDate: string | null;
  kra: string;
  navChangePer: string;
};

type TimeRange = "1w" | "1m" | "3m" | "6m" | "YTD" | "1yr" | "3yr" | "5yr" | "all";

const timeRanges: TimeRange[] = ["1w", "1m", "3m", "6m", "YTD", "1yr", "3yr", "5yr", "all"];

const MS_IN_DAY = 24 * 60 * 60 * 1000;
const MIN_GAP_MS = 7 * MS_IN_DAY;

const getPastDateByCalendar = (range: TimeRange, allDataStart?: number, baseDate?: number): number => {
  const date = new Date(formatDate(baseDate) ?? Date.now());

  switch (range) {
    case "1w":
      date.setDate(date.getDate() - 7);
      break;
    case "1m":
      date.setMonth(date.getMonth() - 1);
      break;
    case "3m":
      date.setMonth(date.getMonth() - 3);
      break;
    case "6m":
      date.setMonth(date.getMonth() - 6);
      break;
    case "YTD":
      date.setMonth(0);
      date.setDate(1);
      return date.getTime();
    case "1yr":
      date.setFullYear(date.getFullYear() - 1);
      break;
    case "3yr":
      date.setFullYear(date.getFullYear() - 3);
      break;
    case "5yr":
      date.setFullYear(date.getFullYear() - 5);
      break;
    case "all":
      return allDataStart ?? date.getTime();
  }

  return date.getTime();
};

const detectRange = (min: number, max: number): string => {
  const duration = max - min;
  const oneDay = 24 * 3600 * 1000;

  const days = duration / oneDay;
  const yearStart = new Date(new Date().getFullYear(), 0, 1).getTime();

  if (Math.abs(min - yearStart) < oneDay * 3) return "YTD";
  if (days <= 8) return "1w";
  if (days <= 32) return "1m";
  if (days <= 95) return "3m";
  if (days <= 185) return "6m";
  if (days <= 370) return "1yr";
  if (days <= 3 * 370) return "3yr";
  if (days <= 5 * 370) return "5yr";

  return "all";
};

const ZoomButtons = ({
  onZoom,
  allDataStart,
  selectedRange,
  minDate,
  maxDate,
}: {
  onZoom: (from: number, to: number, range: TimeRange) => void;
  allDataStart: number;
  selectedRange: string;
  minDate: Date | null;
  maxDate: Date | null;
}) => {
  const [localStart, setLocalStart] = useState<Date | null>(null);
  const [localEnd, setLocalEnd] = useState<Date | null>(null);
  const [isStartCalendarOpen, setIsStartCalendarOpen] = useState(false);
  const [isEndCalendarOpen, setIsEndCalendarOpen] = useState(false);

  const today = new Date();
  const startBtnRef = useRef<HTMLButtonElement>(null);
  const endBtnRef = useRef<HTMLButtonElement>(null);

  const safeAllDataStart = allDataStart || Date.now();

  const setZoom = (range: TimeRange) => {
    const to = Date.now();

    const from = getPastDateByCalendar(range, allDataStart, to);
    onZoom(from, to, range);
  };

  useEffect(() => {
    if (minDate && !isNaN(minDate.getTime())) setLocalStart(minDate);
    if (maxDate && !isNaN(maxDate.getTime())) setLocalEnd(maxDate);
  }, [minDate, maxDate]);

  const handleStartPick = (date: Date) => {
    setLocalStart(date);
    setIsStartCalendarOpen(false);

    let end = localEnd ?? today;
    if (end.getTime() - date.getTime() < MIN_GAP_MS) {
      end = new Date(date.getTime() + MIN_GAP_MS);
      setLocalEnd(end);
    }

    onZoom(date.getTime(), end.getTime(), detectRange(date.getTime(), end.getTime()) as TimeRange);
  };

  const handleEndPick = (date: Date) => {
    setLocalEnd(date);
    setIsEndCalendarOpen(false);

    let start = localStart ?? new Date(allDataStart);
    if (date.getTime() - start.getTime() < MIN_GAP_MS) {
      start = new Date(date.getTime() - MIN_GAP_MS);
      setLocalStart(start);
    }

    onZoom(start.getTime(), date.getTime(), detectRange(start.getTime(), date.getTime()) as TimeRange);
  };

  return (
    <div className="flex flex-col gap-3 relative">
      {/* Predefined Zoom Buttons */}
      <div className="flex xs:justify-center gap-4 px-4 py-1 bg-[#F5F9FF] sm:bg-white border border-[#E4E4E4] sm:border-none rounded-md overflow-x-auto scrollbar-none">
        {timeRanges.map((range) => (
          <button
            key={range}
            className={`text-xs capitalize sm:text-sm py-1.5 px-3 rounded-md transition ${selectedRange === range ? "bg-primaryBlue text-white font-medium" : "text-primaryBlack sm:text-[#949494] hover:bg-[#ddeaff] hover:text-primaryBlue"}`}
            onClick={() => setZoom(range)}
          >
            {range}
          </button>
        ))}
      </div>

      <div className="flex items-center justify-center xxs:justify-end gap-1.5 relative">
        <div className="w-full max-w-32">
          <button
            ref={startBtnRef}
            onClick={() => setIsStartCalendarOpen((p) => !p)}
            className={`w-full py-1 px-1.5 rounded-lg text-left bg-white border text-[#949494] text-2xs font-medium flex items-center justify-between ${isStartCalendarOpen ? "border-[#1a51aa66]" : "border-[#9494941f] hover:border-[#9494944f]"}`}
          >
            {localStart
              ? format(localStart, "dd MMM yyyy")
              : formatDate(new Date(allDataStart), "dd MMM yyyy").toString()}
            <Icon.dropdownDownArrow
              className={`fill-primaryBlack transition ${isStartCalendarOpen ? "rotate-180" : ""}`}
            />
          </button>

          <CustomDropdown
            isOpen={isStartCalendarOpen}
            onClose={() => setIsStartCalendarOpen(false)}
            className="absolute bottom-full right-0 w-full mb-1 rounded-xl border border-[#e4e4e4] bg-white shadow-md z-10 p-1.5 overflow-hidden max-w-80"
            excludeRefs={[startBtnRef]}
          >
            <CustomCalendar
              defaultSelectedDate={localStart ?? today}
              onDateSelect={handleStartPick}
              minSelectableDate={new Date(allDataStart)}
              maxSelectableDate={
                localEnd ? new Date(localEnd.getTime() - MIN_GAP_MS) : new Date(today.getTime() - MIN_GAP_MS)
              }
              fontSizeBase={false}
            />
          </CustomDropdown>
        </div>

        <Icon.rightArrow className="shrink-0 text-[#949494c2]" />

        <div className="w-full max-w-32">
          <button
            ref={endBtnRef}
            onClick={() => setIsEndCalendarOpen((p) => !p)}
            className={`w-full py-1 px-1.5 rounded-lg text-left bg-white border text-[#949494] text-2xs font-medium flex items-center justify-between ${isEndCalendarOpen ? "border-[#1a51aa66]" : "border-[#9494941f] hover:border-[#9494944f]"}`}
          >
            {localEnd ? format(localEnd, "dd MMM yyyy") : formatDate(new Date(), "dd MMM yyyy").toString()}
            <Icon.dropdownDownArrow
              className={`fill-primaryBlack transition ${isEndCalendarOpen ? "rotate-180" : ""}`}
            />
          </button>

          <CustomDropdown
            isOpen={isEndCalendarOpen}
            onClose={() => setIsEndCalendarOpen(false)}
            className="absolute bottom-full right-0 w-full mb-1 rounded-xl border border-[#e4e4e4] bg-white shadow-md z-10 p-1.5 overflow-hidden max-w-80"
            excludeRefs={[endBtnRef]}
          >
            <CustomCalendar
              defaultSelectedDate={localEnd ?? today}
              onDateSelect={handleEndPick}
              minSelectableDate={
                localStart ? new Date(localStart.getTime() + MIN_GAP_MS) : new Date(safeAllDataStart + MIN_GAP_MS)
              }
              maxSelectableDate={today}
              fontSizeBase={false}
            />
          </CustomDropdown>
        </div>
      </div>
    </div>
  );
};

export default function FundVsBenchmarkChart({
  schemeCode,
  schemeName,
  currentNav,
  kra,
  currentNavDate,
  navChangePer,
}: ChartDataType) {
  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
  const [fundReturn, setFundReturn] = useState(0);
  const [benchmarkReturn, setBenchmarkReturn] = useState(0);
  const [selectedRange, setSelectedRange] = useState<string>("");
  const [benchNavData, setBenchNavData] = useState({});
  const [schemeNavData, setSchemeNavData] = useState<[number, number][]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<{ id: number; name: string } | null>({ id: 9, name: "NIFTY 50" });
  const [benchmarkItems, setBenchmarkItems] = useState<AllBenchmarkListItem[]>([]);
  const [minDate, setMinDate] = useState<Date | null>(null);
  const [maxDate, setMaxDate] = useState<Date | null>(null);

  const [isChartLoading, setIsChartLoading] = useState(true);

  const benchDropdownButton = useRef<HTMLButtonElement | null>(null);
  const isFirstLoad = useRef(true);

  useEffect(() => {
    const benchmarkData = async () => {
      try {
        const res = await apiGet("/api/benchmarks", { base: "none" });

        if (res.status != 1) throw new Error("Something went wrong");
        setBenchmarkItems(res.data);
      } catch (e) {
        console.error(e, "Something went wrong !!");
      }
    };
    benchmarkData();
  }, []);

  useEffect(() => {
    const payload = {
      scheme_code: schemeCode,
      kra,
      benchmark_id: selectedItem?.id || 23,
    };

    fetch(`${NODE_API_URL}scheme-details/nav-data`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch");
        return res.json();
      })
      .then((data) => {
        function parseAndFilterData(navData: { date: string; nav: number }[], fromDate: number) {
          return navData
            .map((item) => [new Date(item.date).getTime(), item.nav] as [number, number])
            .filter(([timestamp]) => timestamp >= fromDate);
        }
        const schemeNav = data.result.scheme_nav ?? [];
        const schemeStartDate = new Date(schemeNav[0].date).getTime();

        const fundData = parseAndFilterData(schemeNav, schemeStartDate);
        setSchemeNavData(fundData);
        const benchmarkData = parseAndFilterData(data.result.benchmark_nav, schemeStartDate);

        if (schemeNav[0].date != data.result.benchmark_nav[0].date) benchmarkData.pop();

        setBenchNavData(benchmarkData);

        setTimeout(() => {
          const chart = chartComponentRef.current?.chart;

          if (chart) {
            if (isFirstLoad.current) {
              const to = new Date(schemeNav[schemeNav?.length - 1].date).getTime();
              const from = getPastDateByCalendar("YTD", schemeStartDate, to);

              chart.xAxis[0].setExtremes(from, to);
              setSelectedRange("YTD");

              isFirstLoad.current = false;
            } else {
              const { min, max } = chart.xAxis[0].getExtremes();
              chart.xAxis[0].setExtremes(min, max);

              updateReturnsForCurrentRange(chart);
            }
          }
          setIsChartLoading(false);
        }, 150);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setIsChartLoading(false);
      });
  }, [selectedItem, schemeCode, kra]);

  const truncateLegendText = (text: string, maxCharsPerLine = 18): string => {
    if (text.length <= maxCharsPerLine * 1) return text;

    const line1 = text.slice(0, maxCharsPerLine);
    // const line2 = text.slice(maxCharsPerLine, maxCharsPerLine * 1);
    return `${line1}...`;
  };

  const updateReturnsForCurrentRange = (chart: Highcharts.Chart) => {
    if (!chart) return;

    const [benchmarkSeries, fundSeries] = chart.series;
    const extremes = chart.xAxis[0].getExtremes();
    const { min, max } = extremes;

    const benchmarkChange = calculateReturn(benchmarkSeries.points, min, max);
    const fundChange = calculateReturn(fundSeries.points, min, max);

    setFundReturn(fundChange);
    setBenchmarkReturn(benchmarkChange);
    setSelectedRange(detectRange(min, max));
  };

  const options: Highcharts.Options = {
    chart: {
      height: 300,
      events: {
        load(this: Highcharts.Chart) {
          const [benchmarkSeries, fundSeries] = this.series;

          const extremes = this.xAxis[0].getExtremes();
          const minRange = extremes.min;
          const maxRange = extremes.max;

          setMinDate(new Date(minRange));
          setMaxDate(new Date(maxRange));

          const benchmarkChange = calculateReturn(benchmarkSeries.points, minRange, maxRange);
          const fundChange = calculateReturn(fundSeries.points, minRange, maxRange);
          setFundReturn(fundChange);
          setBenchmarkReturn(benchmarkChange);
          setSelectedRange(detectRange(minRange, maxRange));
        },
      },
    },
    title: { text: "" },
    rangeSelector: { enabled: false },
    scrollbar: { enabled: false },
    navigator: { enabled: false },
    legend: {
      enabled: true,
      layout: "horizontal",
      align: "center",
      verticalAlign: "bottom",
      itemStyle: {
        color: "#949494",
        fontWeight: "500",
        fontSize: "16px",
      },
    },
    xAxis: {
      events: {
        afterSetExtremes(e: Highcharts.AxisSetExtremesEventObject) {
          const axis = e.target as unknown as Highcharts.Axis;
          const chart = axis.chart;
          setMinDate(new Date(e.min));
          setMaxDate(new Date(e.max));
          updateReturnsForCurrentRange(chart);
        },
      },
      gridLineColor: "transparent",
      gridLineWidth: 0,
      lineColor: "transparent",
      lineWidth: 0,
      tickColor: "transparent",
      tickWidth: 0,
      labels: {
        style: {
          color: "#666",
          fontWeight: "100",
        },
      },
    },
    yAxis: {
      title: { text: undefined },
      gridLineColor: "transparent",
      gridLineWidth: 0,
      lineColor: "transparent",
      lineWidth: 0,
      tickColor: "transparent",
      tickWidth: 0,
      labels: {
        style: {
          color: "#666",
          fontWeight: "100",
        },
      },
    },
    tooltip: {
      shared: true,
      valueDecimals: 2,
      pointFormat:
        '<span style="color:{series.color}">\u25CF</span> {series.name}: <b>{point.y}</b> ({point.change}%)<br/>',
    },
    credits: {
      enabled: false,
    },
    series: [
      {
        type: "line",
        name: truncateLegendText(schemeName || "Fund"),
        data: Array.isArray(schemeNavData) ? schemeNavData : [],
        color: "#1E90FF",
        compare: "percent",
      },
      {
        type: "line",
        name: truncateLegendText(selectedItem?.name || "NIFTY 50"),
        data: Array.isArray(benchNavData) ? benchNavData : [],
        color: "#A0A0A0",
        compare: "percent",
      },
    ],
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 480,
          },
          chartOptions: {
            legend: {
              itemStyle: {
                fontSize: "12px",
              },
            },
          },
        },
        {
          condition: {
            maxWidth: 768,
          },
          chartOptions: {
            legend: {
              itemStyle: {
                fontSize: "14px",
              },
            },
          },
        },
        {
          condition: {
            minWidth: 769,
          },
          chartOptions: {
            legend: {
              itemStyle: {
                fontSize: "16px",
              },
            },
          },
        },
      ],
    },
  };

  const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);

  const handleSelect = (id: number, name: string) => {
    setSelectedItem({ id, name });
    setIsDropdownOpen(false);
  };

  const currentNavValue = Number(currentNav);

  return (
    <div className="relative w-full bg-white p-5 sm:p-6 rounded-xl border border-[#E9E9EB]">
      <div className="flex gap-4 flex-wrap justify-between mb-4">
        <div>
          <div className="text-[#444444] text-xs sm:text-sm">
            Current NAV(
            <span className="text-[11px] sm:text-2xs text-[#535353]">
              {formatDate(currentNavDate, "dd MMM yyyy")}
            </span>
            )
          </div>
          <div className={`text-sm sm:text-base font-semibold text-primaryBlack`}>
            ₹{currentNavValue.toFixed(2)}{" "}
            <span
              className={`text-xs sm:text-sm font-medium ${Number(navChangePer) > 0 ? "text-primaryGreen" : "text-[#ff1818]"}`}
            >
              ({navChangePer}%)
            </span>
          </div>
        </div>
        <div>
          <div className="text-[#444444] text-xs sm:text-sm">Returns {selectedRange}</div>
          {/* <div className={`text-sm sm:text-base font-semibold ${fundReturn >= 0 ? "text-green-600" : "text-red-500"}`}>{fundReturn.toFixed(2)}%</div> */}
          <div
            className={`text-sm sm:text-base ${benchmarkReturn >= 0 ? "text-green-600" : "text-red-500"} font-semibold`}
          >
            {benchmarkReturn.toFixed(2)}%
          </div>
        </div>
        <div>
          <div className="text-[#444444] text-xs sm:text-sm">Benchmark Rt {selectedRange}</div>
          {/* <div className={`text-sm sm:text-base ${benchmarkReturn >= 0 ? "text-green-600" : "text-red-500"} font-semibold`}>{benchmarkReturn.toFixed(2)}%</div> */}
          <div className={`text-sm sm:text-base font-semibold ${fundReturn >= 0 ? "text-green-600" : "text-red-500"}`}>
            {fundReturn.toFixed(2)}%
          </div>
        </div>
        <div className="relative">
          <div className="text-[#444444] text-xs sm:text-sm">Compare with</div>

          <button
            ref={benchDropdownButton}
            className="text-sm font-semibold text-[#9F9F9F] border-b border-dashed border-[#9F9F9F] py-1 flex items-center justify-around gap-3"
            onClick={toggleDropdown}
          >
            <span className="w-36 shrink-0 whitespace-nowrap line-clamp-1 text-left">
              {selectedItem ? selectedItem.name : "Nifty 50"}
            </span>
            <svg
              width="8"
              height="7"
              viewBox="0 0 8 7"
              fill="none"
              xmlns="http://www.w3.org/2000/svg "
              className={`transition-transform ${isDropdownOpen ? "rotate-180" : ""}`}
            >
              <path d="M3.75 6.5L7.2141 0.5H0.285898L3.75 6.5Z" fill="#9F9F9F" />
            </svg>
          </button>

          <CustomDropdown
            isOpen={isDropdownOpen}
            onClose={toggleDropdown}
            className="z-1 bg-white border mt-1 rounded-md shadow-lg max-h-60 overflow-y-auto scrollbar-xs"
            excludeRefs={[benchDropdownButton]}
          >
            <>
              {benchmarkItems.map((item: AllBenchmarkListItem) => {
                const label = `${item.stkexchg}`;
                const selected = selectedItem?.id === item.id;

                return (
                  <button
                    key={item.id}
                    onClick={() => handleSelect(item?.id ?? 0, label)}
                    className={`block w-full text-left cursor-pointer divide-y divide-slate-500 px-2 py-1.5 text-sm hover:bg-gray-100 ${selected ? "bg-blue-50 font-medium text-blue-600" : ""}`}
                  >
                    {label}
                  </button>
                );
              })}
            </>
          </CustomDropdown>
        </div>
        <AlertsAndTriggers schemeCode={schemeCode} schemeName={schemeName} defaultPrice={currentNavValue} />
      </div>

      <div className="h-300">
        {isChartLoading ? (
          <div className="flex items-center justify-center h-full">
            <Image
              src="/next_assets/images/gif/chart_loading.gif"
              unoptimized
              alt="Loading Chart Animation"
              loading="eager"
              width={256}
              height={144}
              priority
              className="w-64 h-36 mx-auto"
            />
          </div>
        ) : (
          <HighchartsReact
            highcharts={Highcharts}
            constructorType="stockChart"
            options={options}
            ref={chartComponentRef}
          />
        )}
      </div>

      <ZoomButtons
        onZoom={(from, to, range) => {
          chartComponentRef.current?.chart?.xAxis[0].setExtremes(from, to);
          setSelectedRange(range);
        }}
        allDataStart={schemeNavData?.length ? schemeNavData[0][0] : Date.now()}
        selectedRange={selectedRange}
        minDate={minDate}
        maxDate={maxDate}
      />
    </div>
  );
}
